import { Layout, Text, Card } from '@loadsmart/miranda-react'

import { plural } from '../../utils/string'
import { type ShippingItem } from '../domain/ShippingItem'
import {
  getCommodityDescription,
  getComposeCommodityDescription,
  getStopShippingItems,
  getTotalHandlingUnits,
  getTotalShippingItemsWeight,
} from '../domain/StopShippingItemSummary'
import { formatPOandSONumbers, formatWeightString } from '../presenters/HandlingUnitPresenter'
import {
  formatPackageDescription,
  formatPackageTypeDescription,
  formatShippingItemOrigin,
  formatShippingItemStopName,
} from '../presenters/StopShippingItemSummaryPresenter'

type StopShippingItemSummaryProps = {
  stopType: 'pickup' | 'delivery'
  shippingItems: Array<ShippingItem>
  stopIndex: number
}

type ShippingItemSummaryDescriptionProps = {
  stopType: 'pickup' | 'delivery'
  shippingItem: ShippingItem
  stopIndex: number
}

export function StopShippingItemSummary({
  stopType,
  shippingItems,
  stopIndex,
}: StopShippingItemSummaryProps) {
  const stopShippingItems = getStopShippingItems(shippingItems, stopType, stopIndex)
  const totalUnits = getTotalHandlingUnits(stopShippingItems)
  const totalWeight = getTotalShippingItemsWeight(stopShippingItems)

  return (
    <Layout.Stack gap="spacing-4">
      <Layout.Box padding="none">
        <Text>
          {stopShippingItems.length} {plural('item', stopShippingItems.length)}
        </Text>
        <Text color="color-text-tertiary">
          {' '}
          being {stopType === 'pickup' ? 'picked up' : 'delivered'} at this facility • {totalUnits}{' '}
          H/U • Total weight
        </Text>
        <Text>{formatWeightString(totalWeight)}</Text>
      </Layout.Box>
      <Layout.Group>
        {stopShippingItems.map(shippingItem => (
          <ShippingItemSummaryDescription
            key={`item-${shippingItem.id}-${shippingItem.pickupStopIndex}-${shippingItem.deliveryStopIndex}`}
            stopType={stopType}
            shippingItem={shippingItem}
            stopIndex={stopIndex}
          />
        ))}
      </Layout.Group>
    </Layout.Stack>
  )
}

export function ShippingItemSummaryDescription({
  stopType,
  shippingItem,
  stopIndex,
}: ShippingItemSummaryDescriptionProps) {
  if (stopIndex !== shippingItem.pickupStopIndex && stopIndex !== shippingItem.deliveryStopIndex) {
    return null
  }

  const stopName = formatShippingItemStopName(shippingItem)
  const packageType = formatPackageTypeDescription(shippingItem)

  const commodities = shippingItem.commodities || []

  const firstCommodityDescription = getCommodityDescription(commodities)
  const composeCommodityDescription = getComposeCommodityDescription(
    commodities,
    firstCommodityDescription
  )

  const isPOorSONumberAvailable = shippingItem.poNumber || shippingItem.soNumber

  return (
    <Card style={{ maxWidth: '13rem' }}>
      <Card.Body>
        <Layout.Stack gap="spacing-2">
          {stopType === 'delivery' && (
            <Text color="color-text-tertiary" variant="body-sm">
              {formatShippingItemOrigin(stopName)}
            </Text>
          )}
          <Text variant="body-md-bold">
            {formatPackageDescription(
              packageType,
              composeCommodityDescription,
              shippingItem.commodity,
              firstCommodityDescription
            )}
          </Text>
          {isPOorSONumberAvailable && (
            <Layout.Stack gap="spacing-2">
              <Layout.Group gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  SO Number
                </Text>
                <Text variant="body-sm-bold" color="color-text-primary">
                  {formatPOandSONumbers(shippingItem.soNumber)}
                </Text>
              </Layout.Group>
              <Layout.Group gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  PO Number
                </Text>
                <Text variant="body-sm-bold" color="color-text-primary">
                  {formatPOandSONumbers(shippingItem.poNumber)}
                </Text>
              </Layout.Group>
            </Layout.Stack>
          )}
        </Layout.Stack>
      </Card.Body>
    </Card>
  )
}
